import { useEffect, useRef, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { readError } from '../../lib/errors'

/**
 * Live ops dashboard, mounted at `/admin/live` via main.tsx.
 *
 * Polls admin_live_ops() (is_admin()-gated) every 10s and renders a
 * single-screen snapshot of what's happening right now: humans in play
 * per game, active rooms, matchmaking queue depth, signups, active subs,
 * today's AI spend, and recent moderation reports.
 *
 * This is the game-level view. For raw infrastructure concurrency
 * (websocket connections, DB CPU), use the Supabase dashboard — see the
 * footnote rendered at the bottom.
 */

const REFRESH_MS = 10_000

interface PerGame { ideatthat: number; colorful: number; three_step: number }
interface Snapshot {
  generated_at: string
  active_rooms: PerGame
  players_in_play: PerGame
  queue_depth: PerGame
  signups: { last_hour: number; today: number; total: number }
  subscriptions_active: number
  ai_today: { calls: number; cost_usd: number; errors: number }
  reports_24h: number
}

const GAME_LABEL: Record<keyof PerGame, string> = {
  ideatthat: "I'D EAT THAT",
  colorful: 'COLORFUL',
  three_step: 'Three Step',
}

function sumGames(p: PerGame): number {
  return p.ideatthat + p.colorful + p.three_step
}

export function AdminLive() {
  const { session, profile } = useAuth()
  const [snap, setSnap] = useState<Snapshot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!session) { setLoading(false); return }
    let cancelled = false

    async function load() {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error: e } = await (supabase as any).rpc('admin_live_ops')
        if (cancelled) return
        if (e) throw e
        setSnap(data as Snapshot)
        setError(null)
        setFetchedAt(new Date())
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    timerRef.current = setInterval(load, REFRESH_MS)
    return () => {
      cancelled = true
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [session?.user?.id])

  if (!session) {
    return (
      <Page>
        <h2 className="font-display text-2xl font-bold text-brand-text">Sign in required</h2>
        <p className="text-sm text-brand-muted mt-2">
          Open the app, sign in with your admin account, then return to this URL.
        </p>
      </Page>
    )
  }

  if (loading && !snap) {
    return (
      <Page>
        <div className="space-y-4">
          <div className="h-28 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="grid grid-cols-2 gap-3">
            <div className="h-28 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-28 rounded-2xl bg-brand-elevated animate-pulse" />
          </div>
        </div>
      </Page>
    )
  }

  if (error && !snap) {
    return (
      <Page>
        <div className="card p-5 bg-brand-danger/10 border-brand-danger/30 border">
          <p className="font-semibold text-brand-danger">Error loading live ops</p>
          <p className="text-sm text-brand-text mt-1">{error}</p>
          <p className="text-xs text-brand-muted mt-3">
            If this says "Admin only", you're signed in as a non-admin account.
          </p>
        </div>
      </Page>
    )
  }

  if (!snap) return <Page><div /></Page>

  const playingTotal = sumGames(snap.players_in_play)
  const roomsTotal = sumGames(snap.active_rooms)
  const queueTotal = sumGames(snap.queue_depth)

  return (
    <Page>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-black text-brand-text">Live ops</h1>
          <p className="text-sm text-brand-muted mt-1">
            Signed in as {profile?.display_name ?? 'admin'} · auto-refreshing every 10s
          </p>
        </div>
        <span className="text-xs text-brand-muted tabular-nums">
          {fetchedAt ? `updated ${fetchedAt.toLocaleTimeString()}` : ''}
        </span>
      </div>

      {/* Hero: humans in play right now */}
      <div className="card p-6 mb-4 bg-linear-to-br from-brand-text to-gray-800 text-white">
        <p className="text-xs font-semibold uppercase tracking-widest text-white/60">
          Players in a game right now
        </p>
        <p className="font-display text-6xl font-black mt-1 tabular-nums">{playingTotal}</p>
        <div className="flex gap-4 mt-3 text-sm text-white/80">
          {(Object.keys(GAME_LABEL) as (keyof PerGame)[]).map(g => (
            <span key={g}>
              {GAME_LABEL[g]}: <span className="font-bold text-white">{snap.players_in_play[g]}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <Stat label="Active rooms" value={roomsTotal} sub={perGameSub(snap.active_rooms)} />
        <Stat label="In matchmaking" value={queueTotal} sub={perGameSub(snap.queue_depth)} />
        <Stat label="Signups · last hour" value={snap.signups.last_hour}
              sub={`${snap.signups.today} today · ${snap.signups.total.toLocaleString()} total`} />
        <Stat label="Active subscriptions" value={snap.subscriptions_active} />
        <Stat label="AI calls today" value={snap.ai_today.calls}
              sub={`$${snap.ai_today.cost_usd.toFixed(2)} spend today`}
              danger={snap.ai_today.errors > 0
                ? `${snap.ai_today.errors} error${snap.ai_today.errors === 1 ? '' : 's'}` : undefined} />
        <Stat label="Reports · 24h" value={snap.reports_24h}
              danger={snap.reports_24h > 0 ? 'needs review' : undefined} />
      </div>

      {error && (
        <p className="text-xs text-brand-danger mb-3">
          Last refresh failed: {error} (showing previous snapshot)
        </p>
      )}

      <div className="card p-4 bg-brand-elevated">
        <p className="text-xs font-semibold text-brand-text mb-1">Where this comes from</p>
        <p className="text-xs text-brand-muted leading-relaxed">
          These are game-level counts from the database. For raw infrastructure
          concurrency (live websocket connections, database CPU / memory, API
          request rate), use the Supabase dashboard: Reports → Realtime and
          Reports → Database. That connection count is the true ceiling to watch.
        </p>
      </div>
    </Page>
  )
}

function perGameSub(p: PerGame): string {
  return `IET ${p.ideatthat} · Col ${p.colorful} · 3S ${p.three_step}`
}

function Stat({
  label, value, sub, danger,
}: {
  label: string
  value: number
  sub?: string
  danger?: string
}) {
  return (
    <div className="card p-4">
      <p className="text-xs font-medium text-brand-muted uppercase tracking-wide">{label}</p>
      <p className="font-display text-3xl font-black text-brand-text mt-1 tabular-nums">
        {value.toLocaleString()}
      </p>
      {sub && <p className="text-xs text-brand-muted mt-1">{sub}</p>}
      {danger && <p className="text-xs font-semibold text-brand-danger mt-1">{danger}</p>}
    </div>
  )
}

function Page({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-brand-bg pb-20">
      <div className="mx-auto max-w-2xl px-5 py-8">
        {children}
      </div>
    </div>
  )
}
